/**
 * ResultDisplay.tsx — v0.4.9.3.5
 *
 * Extracted from civilRespondCheck.tsx (was inline at lines 394–606). This is
 * the result view shown after `runCivilCheck` resolves.
 *
 * v0.4.9.3.5 — four changes baked in:
 *   1. Image moves ABOVE the result text (was below the name + ocr_plate).
 *      Locked: the officer should see the cropped plate they just took before
 *      any verdict text.
 *   2. Edit button reachable in BOTH branches (match-found AND no-match).
 *      On save, calls `api.correctPlate(action_id, plate, 'corrected')`. The
 *      backend re-runs the lookup, promotes the cropped image to a training
 *      asset, and returns the new match. We update the displayed result with
 *      the new match so the officer sees the corrected read without leaving
 *      the page.
 *   3. False-positive form: inline expansion under "False positive" tap.
 *      Three reasons per user (Misread / Wrong vehicle / Other). "Other"
 *      reveals a free-text field. Submit calls `api.correctPlate(action_id,
 *      plate, 'false_positive')`. Officer stays on the result page.
 *   4. Existing FP, Take Another, Back buttons preserved.
 *
 * The cropped plate image (capture_image_url) is handled by the backend via
 * `promoteToTrainingAsset` (checks.js line 142) — when `correctPlate` is
 * called, the working copy in `/var/www/uploads/civil-respondent-checks/`
 * is moved into `/var/www/uploads/.../training/` and the new URL is written
 * to `plate_reading_corrections.capture_image_url`. No upload work is
 * needed in the frontend — we just call the endpoint with the typed plate.
 *
 * Props:
 *   result         — the run-check response (has action_id, ocr_plate,
 *                    match, score, snapshot_url, message)
 *   targetType     — 'missing_person' | 'most_wanted' | 'stolen_vehicle'
 *   onAnother      — "Take Another" handler (resets the check flow)
 *   onBack         — "Back" handler (navigates to /civil-respondent)
 *
 * v0.4.9.3.5c1: the FP button is handled INTERNAL via CorrectionPanel.
 * The legacy `onFalsePositive` prop is gone.
 *
 * i18n keys used (added in v0.4.9.3.5):
 *   civil.check.edit
 *   civil.check.editPlaceholder
 *   civil.check.editSave
 *   civil.check.editCancel
 *   civil.check.fpReasons.misread
 *   civil.check.fpReasons.wrong
 *   civil.check.fpReasons.other
 *   civil.check.fpNotesPlaceholder
 *   civil.check.correctionSaved
 *   civil.check.correctionFailed
 *   common.cancel
 *   common.reset
 *
 * The keys exist in the existing /locales/{{lng}}/translation.json files —
 * the translation round (v0.4.9.3.5c2) is scoped out of this delivery.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle, AlertCircle, RotateCcw, Pencil, X, Check, Phone, Crop as CropIcon,
} from 'lucide-react';
import { api } from '../services/api';

type TargetType = 'missing_person' | 'most_wanted' | 'stolen_vehicle';

interface ResultDisplayProps {
  result: any;
  targetType: TargetType;
  onAnother: () => void;
  onBack: () => void;
}

type CorrectionIntent = 'corrected' | 'false_positive';

interface CorrectionPanelProps {
  actionId: string;
  defaultPlate: string;
  /** "corrected" = Edit (re-runs lookup with the typed plate).
   *  "false_positive" = FP form (with reason). */
  intent: CorrectionIntent;
  /** Called when the backend returns a new match. ResultDisplay swaps in. */
  onCorrectionApplied: (newResult: any) => void;
  /** Called to close the inline form (e.g. Cancel button). */
  onClose: () => void;
}

/**
 * Inline Edit / FP panel. Two modes via the `intent` prop — both submit to
 * the same backend endpoint, with different `intent` values.
 *
 * Edit (intent='corrected'):
 *   - Text input pre-filled with the officer's typed plate.
 *   - Save → POST /actions/:id/plate-correction with {plate, intent: 'corrected'}
 *   - Backend re-runs lookup, returns new match. We swap the displayed result.
 *
 * FP (intent='false_positive'):
 *   - Three reasons (Misread / Wrong vehicle / Other). "Other" reveals a free-text field.
 *   - Save → POST /actions/:id/plate-correction with {plate, intent: 'false_positive'}
 *   - Backend records the FP, promotes the working image to a training asset,
 *     and returns the new match (in case the officer's typed plate surfaces
 *     a different stolen vehicle). We swap the result display.
 */
function CorrectionPanel({
  actionId, defaultPlate, intent, onCorrectionApplied, onClose,
}: CorrectionPanelProps) {
  const { t } = useTranslation();
  const [plate, setPlate] = useState(defaultPlate);
  const [reason, setReason] = useState<'misread' | 'wrong' | 'other'>('misread');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isFp = intent === 'false_positive';

  async function handleSave() {
    const trimmed = plate.trim();
    if (!trimmed) {
      setError(t('civil.check.err.correctionEmpty'));
      return;
    }
    if (isFp && reason === 'other' && !notes.trim()) {
      setError(t('civil.check.err.fpOtherEmpty'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const serverIntent: 'corrected' | 'false_positive' | 'manual' =
        isFp ? 'false_positive' : 'corrected';
      const res = await api.correctPlate(actionId, trimmed, serverIntent);
      onCorrectionApplied(res);
    } catch (err: any) {
      setError(err?.message || t('civil.check.correctionFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mt-4 p-3 bg-card rounded-lg border border-border">
      {isFp ? (
        <>
          <div className="text-sm font-medium text-foreground mb-2">
            {t('civil.check.fpPrompt')}
          </div>
          <div className="space-y-1.5 mb-3">
            {(['misread', 'wrong', 'other'] as const).map((r) => (
              <label key={r} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name={`fp-${actionId}`}
                  checked={reason === r}
                  onChange={() => setReason(r)}
                  className="accent-primary"
                />
                {t(`civil.check.fpReasons.${r}`)}
              </label>
            ))}
          </div>
          {reason === 'other' && (
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('civil.check.fpNotesPlaceholder')}
              className="w-full px-3 py-2 mb-3 border border-input rounded bg-background text-foreground text-sm"
            />
          )}
          <div className="text-xs text-muted-foreground mb-1">
            {t('civil.check.fpPlatePrompt')}
          </div>
        </>
      ) : (
        <div className="text-sm font-medium text-foreground mb-2">
          {t('civil.check.editPrompt')}
        </div>
      )}

      <input
        type="text"
        value={plate}
        onChange={(e) => setPlate(e.target.value)}
        placeholder={t('civil.check.editPlaceholder')}
        className="w-full px-3 py-2 mb-3 border border-input rounded bg-background text-foreground text-sm uppercase"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
      />

      {error && (
        <div className="text-sm text-destructive mb-2">{error}</div>
      )}

      <div className="flex gap-2">
        <button
          onClick={onClose}
          disabled={submitting}
          className="flex-1 py-2 rounded border border-input text-foreground hover:bg-background disabled:opacity-50 text-sm"
        >
          {t('civil.check.editCancel')}
        </button>
        <button
          onClick={handleSave}
          disabled={submitting}
          className="flex-1 py-2 rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-1"
        >
          <Check className="w-4 h-4" />
          {submitting ? t('common.saving') : t('civil.check.editSave')}
        </button>
      </div>
    </div>
  );
}

export default function ResultDisplay({
  result, targetType, onAnother, onBack,
}: ResultDisplayProps) {
  const { t } = useTranslation();
  const [isFlipped, setIsFlipped] = useState(false);
  const [acting, setActing] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [reportingFp, setReportingFp] = useState(false);

  // The result object is mutable across corrections: when the officer Edits,
  // the backend returns a new match and we swap it into display state. Same
  // for FP. So we keep a local `displayResult` that we update.
  const [displayResult, setDisplayResult] = useState(result);
  // v0.4.9.3.5c1: useState's initial value only runs on first render. If
  // the parent passes a new `result` prop (e.g., officer takes a new
  // snapshot and runs a fresh check), the local displayResult would stay
  // pinned to the old object. Sync on prop change.
  useEffect(() => { setDisplayResult(result); }, [result]);

  const match = displayResult.match;
  const matched = !!match;
  const onFileImage = match
    ? (match.face_photo_url || match.photo_url)
    : null;
  const snapshotImage = displayResult.snapshot_url || null;

  // After Edit/FP, the backend returns a fresh result. v0.4.9.3.5c1: merge
  // it into displayResult rather than replacing — the backend response is
  // a "what changed" view and may omit fields like snapshot_url in some
  // branches. Merging preserves the image and any other fields the
  // backend doesn't echo back.
  function onCorrectionApplied(newResult: any) {
    setDisplayResult((prev: any) => ({ ...prev, ...newResult }));
    setEditing(false);
    setReportingFp(false);
    // Clear the flip state — the new image might differ from the old.
    setIsFlipped(false);
  }

  async function handleAction(endpoint: 'family-contacted' | 'action-completed') {
    if (!displayResult?.action_id) return;
    setActing(endpoint);
    try {
      if (endpoint === 'family-contacted') {
        await api.markActionFamilyContacted(displayResult.action_id);
      } else {
        await api.markActionCompleted(displayResult.action_id);
      }
      onBack();
    } catch (err: any) {
      alert(t('common.errorWithColon') + ' ' + (err?.message || t('common.unknown')));
    } finally {
      setActing(null);
    }
  }

  // The default text for the Edit input is the OCR's read. If the OCR
  // returned nothing, it's empty — the officer types from scratch.
  const defaultEditPlate: string = displayResult?.ocr_plate || '';

  return (
    <div className="w-full">
      {matched ? (
        <div className="bg-card border-2 border-primary rounded-2xl p-6 shadow-lg">
          {/* v0.4.9.3.5: IMAGE MOVED ABOVE the result text (was below the
              name in the v0.4.9.3.4 inline version). Locked: the officer
              should see what they just took before any verdict text. */}
          {(snapshotImage || onFileImage) && (
            <div className="mb-4 aspect-square bg-muted rounded-lg relative overflow-hidden">
              <button
                onClick={() => setIsFlipped((f) => !f)}
                className="absolute top-2 left-2 z-10 bg-black/60 text-white rounded-full p-2 hover:bg-black/80 transition-colors"
                title={isFlipped ? t('civil.check.showOnFile') : t('civil.check.showSnapshot')}
              >
                <RotateCcw className="w-5 h-5" />
              </button>

              {!displayResult.ocr_plate && (
                <div className="absolute top-2 right-2 z-10 bg-black/60 text-white rounded-full px-3 py-1 text-sm font-bold">
                  {(displayResult.score * 100).toFixed(1)}% {t('civil.check.matchSuffix')}
                </div>
              )}

              <div className="w-full h-full relative">
                <div className={`absolute inset-0 transition-opacity duration-500 ${isFlipped ? 'opacity-0' : 'opacity-100'}`}>
                  {snapshotImage ? (
                    <img src={snapshotImage} alt={t('civil.check.snapshotTaken')} className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">{t('civil.check.noSnapshot')}</div>
                  )}
                </div>
                <div className={`absolute inset-0 transition-opacity duration-500 ${isFlipped ? 'opacity-100' : 'opacity-0'}`}>
                  {onFileImage ? (
                    <img src={onFileImage} alt={t('civil.check.onFile')} className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">{t('civil.check.noOnFile')}</div>
                  )}
                </div>
              </div>

              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-white/80 text-xs bg-black/40 px-2 py-0.5 rounded">
                {isFlipped ? t('civil.check.showingOnFile') : t('civil.check.showingSnapshot')}
              </div>
            </div>
          )}

          {/* Header: match status + name */}
          <div className="flex items-center gap-2 text-primary font-semibold mb-2">
            <CheckCircle className="w-6 h-6" />
            {t('civil.check.matchFound')}
          </div>
          <div className="text-2xl font-bold text-foreground mb-1">{match.full_name}</div>
          <div className="text-sm text-muted-foreground mb-3">
            {displayResult.ocr_plate ? `${t('civil.check.ocrPlate')}: ${displayResult.ocr_plate}` : `${t('civil.common.matchScore')}: ${(displayResult.score * 100).toFixed(1)}%`}
          </div>

          {/* Details */}
          <div className="space-y-2 text-sm">
            {match.country && <Field label={t('civil.check.field.country')} value={match.country} />}
            {match.city && <Field label={t('civil.check.field.city')} value={match.city} />}
            {match.age && <Field label={t('civil.check.field.age')} value={String(match.age)} />}
            {match.gender && <Field label={t('civil.check.field.gender')} value={match.gender} />}
            {match.description && <Field label={t('civil.check.field.description')} value={match.description} />}
            {match.last_seen_at && <Field label={t('civil.check.field.lastSeen')} value={new Date(match.last_seen_at).toLocaleString()} />}
            {match.crime_committed && <Field label={t('civil.check.field.crime')} value={match.crime_committed} />}
            {match.reporting_station && <Field label={t('civil.check.field.reportingStation')} value={match.reporting_station} />}
            {match.wanted_in && <Field label={t('civil.check.field.wantedIn')} value={match.wanted_in} />}
            {match.recommended_action && <Field label={t('civil.check.field.recommendedAction')} value={match.recommended_action} />}
            {match.license_plate && <Field label={t('civil.check.field.licensePlate')} value={match.license_plate} />}
            {match.make && <Field label={t('civil.check.field.make')} value={match.make} />}
            {match.model && <Field label={t('civil.check.field.model')} value={match.model} />}
            {match.color && <Field label={t('civil.check.field.color')} value={match.color} />}
            {match.year && <Field label={t('civil.check.field.year')} value={String(match.year)} />}
          </div>

          {/* Contact info (for missing persons) */}
          {match.contact_info && (
            <div className="mt-4 p-3 bg-card rounded-lg border border-primary/30">
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">{t('civil.check.contactInfo')}</div>
              {match.contact_info.name && <Field label={t('civil.check.field.name')} value={match.contact_info.name} />}
              {match.contact_info.phone && (
                <div className="flex items-center justify-between py-1">
                  <div>
                    <div className="text-xs text-muted-foreground">{t('civil.check.field.phone')}</div>
                    <div className="text-sm font-medium text-foreground">{match.contact_info.phone}</div>
                  </div>
                  <a
                    href={`tel:${match.contact_info.phone}`}
                    className="flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90"
                  >
                    <Phone className="w-4 h-4" /> {t('civil.check.call')}
                  </a>
                </div>
              )}
              {match.contact_info.relation && <Field label={t('civil.check.field.relation')} value={match.contact_info.relation} />}
            </div>
          )}

          {/* Red notice link (for most wanted) */}
          {match.red_notice_url && (
            <div className="mt-3">
              <a
                href={match.red_notice_url}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-primary hover:underline"
              >
                {t('civil.check.viewRedNotice')}
              </a>
            </div>
          )}

          {/* v0.4.9.3.5: Edit button. Reachable from BOTH branches. On save,
              the backend re-runs the lookup with the officer's plate and
              we update `displayResult` to show the new outcome. */}
          {!editing && (
            <div className="mt-4">
              <button
                onClick={() => { setEditing(true); setReportingFp(false); }}
                className="w-full py-2 rounded-lg border border-input text-foreground hover:bg-background flex items-center justify-center gap-2 text-sm"
              >
                <Pencil className="w-4 h-4" /> {t('civil.check.edit')}
              </button>
            </div>
          )}
          {editing && displayResult.action_id && (
            <CorrectionPanel
              actionId={displayResult.action_id}
              defaultPlate={defaultEditPlate}
              intent="corrected"
              onCorrectionApplied={onCorrectionApplied}
              onClose={() => setEditing(false)}
            />
          )}

          {/* Action taken: Family Contacted (missing persons only) or Action Completed. */}
          {(targetType === 'missing_person' || targetType === 'most_wanted') && (
            <div className="mt-5 space-y-2">
              {targetType === 'missing_person' && match.contact_info && (
                <button
                  onClick={() => handleAction('family-contacted')}
                  disabled={acting !== null}
                  className="w-full py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 font-medium"
                >
                  {acting === 'family-contacted' ? t('common.saving') : t('civil.check.familyContacted')}
                </button>
              )}
              <button
                onClick={() => handleAction('action-completed')}
                disabled={acting !== null}
                className="w-full py-3 rounded-lg bg-foreground text-background hover:opacity-90 disabled:opacity-50 font-medium"
              >
                {acting === 'action-completed' ? t('common.saving') : t('civil.check.actionCompleted')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-accent dark:bg-amber-950/30 border-2 border-border rounded-2xl p-6 shadow-lg">
          {/* v0.4.9.3.5: even the no-match branch shows the image (above
              the alert), so the officer can confirm what they took. */}
          {snapshotImage && (
            <div className="mb-4 aspect-square bg-muted rounded-lg overflow-hidden">
              <img src={snapshotImage} alt={t('civil.check.snapshotTaken')} className="w-full h-full object-contain" />
            </div>
          )}
          <div className="flex items-center gap-2 text-foreground dark:text-amber-300 font-semibold mb-2">
            <AlertCircle className="w-6 h-6" />
            {t('civil.check.noMatch')}
          </div>
          <p className="text-sm text-foreground mb-3">
            {displayResult.message || t('civil.check.noMatchInDb')}
          </p>

          {/* v0.4.9.3.5: Edit-on-no-plate. If OCR returned nothing or a
              non-plate string, the officer types the plate here. Backend
              re-runs the lookup with the typed plate; we update the
              result display. */}
          {!editing && (
            <div className="mt-3">
              <button
                onClick={() => { setEditing(true); setReportingFp(false); }}
                className="w-full py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 flex items-center justify-center gap-2 text-sm font-medium"
              >
                <Pencil className="w-4 h-4" /> {t('civil.check.edit')}
              </button>
            </div>
          )}
          {editing && displayResult.action_id && (
            <CorrectionPanel
              actionId={displayResult.action_id}
              defaultPlate={defaultEditPlate}
              intent="corrected"
              onCorrectionApplied={onCorrectionApplied}
              onClose={() => setEditing(false)}
            />
          )}
        </div>
      )}

      <div className="mt-6 flex gap-3">
        <button
          onClick={onAnother}
          className="flex-1 py-3 rounded-lg border border-border text-foreground hover:bg-accent flex items-center justify-center gap-2"
        >
          <RotateCcw className="w-4 h-4" /> {t('civil.check.takeAnother')}
        </button>
        <button
          onClick={() => { setReportingFp(true); setEditing(false); }}
          className="flex-1 py-3 rounded-lg border border-border bg-accent dark:bg-amber-950/30 text-foreground dark:text-amber-300 hover:bg-accent dark:hover:bg-amber-950/50"
        >
          {t('civil.check.falsePositive')}
        </button>
        <button
          onClick={onBack}
          className="flex-1 py-3 rounded-lg bg-foreground text-background hover:opacity-90"
        >
          {t('common.back')}
        </button>
      </div>

      {/* v0.4.9.3.5: FP form below the action row. Reachable from both
          branches. On submit, the officer stays on the page (per Q5). */}
      {reportingFp && displayResult.action_id && (
        <div className="mt-3">
          <CorrectionPanel
            actionId={displayResult.action_id}
            defaultPlate={defaultEditPlate}
            intent="false_positive"
            onCorrectionApplied={onCorrectionApplied}
            onClose={() => setReportingFp(false)}
          />
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-1">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium text-foreground text-right">{value}</div>
    </div>
  );
}
